import React from "react";
import { Link } from "react-router-dom";
import { PageHero, SectionHeader, SiteLayout } from "./components/SiteLayout";
import { deliverySteps, modelSafeguards } from "./siteData";

const graduateChallenges = [
  "Difficulty finding meaningful first jobs",
  "Limited opportunities to apply theoretical knowledge",
  "Lack of industry-specific skills and practices",
  "Few chances to work with experienced professionals",
];

const employerChallenges = [
  "Risk in hiring inexperienced talent",
  "Resources required for training",
  "Productivity concerns during learning curve",
  "Difficulty assessing potential without track record",
];

const benefits = [
  {
    title: "For Clients",
    points: [
      "Fresh perspectives and innovative approaches",
      "Cost-effective solutions with quality assurance",
      "Supporting workforce development",
      "Access to diverse skill sets",
    ],
  },
  {
    title: "For Graduates",
    points: [
      "Real-world project experience",
      "Mentorship from industry experts",
      "Portfolio development",
      "Professional network building",
    ],
  },
  {
    title: "For Mentors",
    points: [
      "Opportunity to give back to the industry",
      "Leadership and teaching experience",
      "Exposure to new technologies and approaches",
      "Professional network expansion",
    ],
  },
];

const modelPillars = [
  {
    title: "Commercial Services",
    description: "We provide high-quality engineering solutions across software, hardware, and IT domains to clients, generating revenue while creating real-world project opportunities.",
  },
  {
    title: "Talent Development",
    description: "We hire recent graduates and provide them with structured opportunities to work on real projects under expert guidance, building their skills and experience.",
  },
  {
    title: "Mentorship Network",
    description: "Experienced industry professionals work with us as mentors and advisors, guiding junior team members and ensuring quality deliverables.",
  },
  {
    title: "Project Allocation",
    description: "We maintain a mix of commercial and volunteer projects, allocating them based on team skills, interests, and development needs.",
  },
];

export default function ModelPage() {
  return (
    <SiteLayout title="Our Model" description="How SaskTech Lab combines delivery quality with mentorship.">
      <PageHero
        eyebrow="Our Unique Model"
        title="Bridging the gap between education and industry through mentorship and real-world projects."
        description="SaskTech Lab addresses the experience gap challenge through a unique business model that balances commercial engineering services with structured talent development."
        actions={[
          <Link key="contact" to="/contact" className="button-tertiary">
            Ask about the model
          </Link>,
        ]}
      />

      {/* The Problem */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="The Experience Gap Challenge" title="The problem we're solving." description="Many talented graduates face a common paradox when entering the job market: they need experience to get a job, but need a job to gain experience." />
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <div className="panel-card h-full">
            <h3 className="font-display text-2xl font-bold text-emerald-950">For Graduates</h3>
            <ul className="mt-6 space-y-3 text-sm text-slate-600">
              {graduateChallenges.map((item) => (
                <li key={item} className="list-bullet-item">{item}</li>
              ))}
            </ul>
          </div>
          <div className="panel-card h-full">
            <h3 className="font-display text-2xl font-bold text-emerald-950">For Employers</h3>
            <ul className="mt-6 space-y-3 text-sm text-slate-600">
              {employerChallenges.map((item) => (
                <li key={item} className="list-bullet-item">{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Our Solution */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Our Solution" title="A dual-purpose business model." description="SaskTech Lab addresses this challenge through a unique business model that balances commercial engineering services with structured talent development." />
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {modelPillars.map((pillar) => (
            <article key={pillar.title} className="panel-card h-full">
              <h3 className="font-display text-xl font-bold text-emerald-950">{pillar.title}</h3>
              <p className="mt-4 text-sm leading-7 text-slate-600">{pillar.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="How Our Model Works" title="A structured, five-stage process from project to career." description="The process is simple enough to understand and structured enough to trust." />
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-5">
          {deliverySteps.map((step, index) => (
            <article key={step.title} className="panel-card h-full">
              <p className="font-display text-4xl font-bold text-emerald-200">0{index + 1}</p>
              <h3 className="mt-4 text-xl font-semibold text-emerald-950">{step.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Safeguards */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Quality Safeguards" title="Quality is protected through review, structure, and accountability." description="Our mentorship model ensures that junior talent is always guided by experienced professionals." />
        <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {modelSafeguards.map((item) => (
            <div key={item} className="panel-card text-sm leading-7 text-slate-600">
              {item}
            </div>
          ))}
        </div>
      </section>

      {/* Who Benefits */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Who Benefits" title="The model works because it creates value on multiple sides." description="Clients, graduates, and mentors all gain something meaningful from this arrangement." />
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {benefits.map((group) => (
            <article key={group.title} className="panel-card h-full">
              <h3 className="font-display text-2xl font-bold text-emerald-950">{group.title}</h3>
              <ul className="mt-6 space-y-3 text-sm text-slate-600">
                {group.points.map((point) => (
                  <li key={point} className="list-bullet-item">{point}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* Join Our Mission */}
      <section className="site-shell section-space pb-10">
        <div className="rounded-[36px] border border-emerald-900/10 bg-gradient-to-br from-white via-emerald-50 to-emerald-100 p-8 lg:p-10">
          <SectionHeader eyebrow="Join Our Mission" title="Be part of our journey to transform engineering education and practice." description="" />
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact" className="button-tertiary">Work With Us</Link>
            <Link to="/careers" className="button-secondary">Join Our Team</Link>
            <Link to="/contact" className="button-secondary">Become a Mentor</Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}